import { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { SUPPORTED_LANGUAGES, LanguageConfig } from '../../lib/languages';
import { usePreferences } from '../../contexts/PreferencesContext';
import { SupportedLanguage } from '../../lib/expenseTypeHelpers';
import { useTranslations } from '../../i18n';
import { LanguagePickerModal } from './LanguagePickerModal';

type LanguageSelectorProps = {
    variant?: 'compact' | 'full' | 'settings' | 'ghost' | 'nav';
    standalone?: boolean; // If true, manages its own state (for login screen before auth)
    onLanguageChange?: (lang: SupportedLanguage) => void;
    currentLanguage?: SupportedLanguage;
    useModal?: boolean;
    className?: string;
};

export function LanguageSelector({
    variant = 'compact',
    standalone = false,
    onLanguageChange,
    currentLanguage: propCurrentLanguage,
    useModal = false,
    className = ''
}: LanguageSelectorProps) {
    const { preferences, updatePreferences } = usePreferences();
    const { t } = useTranslations();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Determine current language: prop -> preferences -> default 'en'
    // Note: preferences might be null if not logged in, so we handle that.
    const currentLangCode = propCurrentLanguage || preferences?.language || 'en';
    const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === currentLangCode) || SUPPORTED_LANGUAGES[0];

    useEffect(() => {
        // If using modal, the modal handles its own closing via backdrop
        if (useModal) return;

        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [useModal]);

    const handleSelect = async (lang: LanguageConfig) => {
        if (standalone) {
            if (onLanguageChange) onLanguageChange(lang.code);
        } else {
            await updatePreferences({ language: lang.code });
        }
        setIsOpen(false);
    };

    // ... inside LanguageSelector component

    if (variant === 'settings') {
        return (
            <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 mb-1">
                    <Globe size={18} />
                    <label className="text-[11px] font-black uppercase tracking-widest ml-1">
                        {t.settings.language}
                    </label>
                </div>

                <div className="relative group">
                    <select
                        value={currentLangCode}
                        onChange={(e) => {
                            const lang = SUPPORTED_LANGUAGES.find(l => l.code === e.target.value);
                            if (lang) handleSelect(lang);
                        }}
                        className={`w-full bg-white dark:bg-slate-900 border-2 border-gray-100 dark:border-slate-800 rounded-xl px-4 py-3 pl-12 text-sm font-bold appearance-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all outline-none cursor-pointer text-gray-900 dark:text-white`}
                    >
                        {SUPPORTED_LANGUAGES.map((lang) => (
                            <option key={lang.code} value={lang.code} className="text-gray-900 dark:text-white bg-white dark:bg-slate-900">
                                {lang.nativeName} ({lang.name})
                            </option>
                        ))}
                    </select>

                    {/* Selected Flag */}
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-xl">
                        {currentLang.flag}
                    </div>

                    {/* Chevron */}
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-600 group-hover:text-blue-500 transition-colors">
                        <ChevronDown size={20} strokeWidth={2.5} />
                    </div>
                </div>
            </div>
        );
    }

    const buttonStyles = {
        compact: 'px-3 py-2 bg-slate-900/50 backdrop-blur border border-white/10 rounded-lg hover:bg-white/5 text-sm text-slate-300',
        full: 'px-4 py-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 w-full justify-between text-slate-300',
        ghost: 'p-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-all text-gray-600 dark:text-gray-300',
        nav: 'p-2.5 flex items-center text-blue-600 dark:text-blue-400 transition-all'
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 transition-all ${buttonStyles[variant as keyof typeof buttonStyles] || buttonStyles.compact} ${className}`}
            >
                <div className="flex items-center gap-2">
                    {variant === 'nav' ? (
                        <Globe size={28} />
                    ) : (
                        <span className="text-lg leading-none">{currentLang.flag}</span>
                    )}
                    {variant !== 'ghost' && variant !== 'nav' && (
                        <span className="font-medium">
                            {variant === 'full' ? currentLang.nativeName : currentLang.code.toUpperCase()}
                        </span>
                    )}
                </div>
                {variant !== 'ghost' && variant !== 'nav' && (
                    <ChevronDown size={14} className={`text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                )}
            </button>

            {useModal ? (
                <LanguagePickerModal
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    onSelect={(lang) => {
                        const config = SUPPORTED_LANGUAGES.find(l => l.code === lang);
                        if (config) handleSelect(config);
                    }}
                    currentLanguage={currentLangCode}
                    title={t.settings.language}
                />
            ) : isOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-slate-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 max-h-80 overflow-y-auto">
                    <div className="p-1">
                        {SUPPORTED_LANGUAGES.map(lang => (
                            <button
                                key={lang.code}
                                onClick={() => handleSelect(lang)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${currentLangCode === lang.code
                                    ? 'bg-blue-600 text-white'
                                    : 'text-slate-400 hover:bg-white/5 hover:text-white'
                                    }`}
                            >
                                <span className="text-lg min-w-[24px] text-center">{lang.flag}</span>
                                <div className="flex-1 text-left">
                                    <div className="font-medium text-sm">{lang.nativeName}</div>
                                    <div className="text-xs opacity-60">{lang.name}</div>
                                </div>
                                {currentLangCode === lang.code && <Check size={14} />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
